<script setup lang="ts">
import { ref } from 'vue';
import { formatPhoneNumber } from '@Helpers';

const emit = defineEmits<{
  onUsePerson: [result: any];
  onClose: [];
}>();

const props = withDefaults(
  defineProps<{
    mode: string;
    results: any;
  }>(),
  {
    mode: 'fill',
  }
);

const results = ref(props.results);

function handleClose() {
  results.value = [];
  emit('onClose');
}
</script>
<template>
  <div
    v-if="results.length"
    class="align-center fixed left-0 top-0 z-[99999] flex h-screen w-screen flex-col items-center justify-center bg-black bg-opacity-40"
  >
    <div class="m-4 w-fit rounded-xl bg-white p-4">
      <Message
        v-if="results.length"
        class="mt-0 flex justify-center"
        id="person-help"
        :closable="false"
        :severity="mode === 'link' ? 'error' : 'warn'"
      >
        {{ $t('components.forms.errors.duplicateWarning') }}!
      </Message>

      <div v-if="mode === 'link'">
        <div
          v-if="results.length"
          class="flex items-center justify-center gap-2 text-sm font-semibold text-red-600"
        >
          <p>
            {{
              $t('components.forms.errors.duplicatedPerson', { person: results.length }, results.length) +
              '. ' +
              $t('components.formFields.contact.selectToGo')
            }}.
          </p>

          <i class="pi pi-external-link text-sm"></i>
        </div>

        <div
          class="mt-4 grid h-fit max-h-96 grid-cols-2 items-center justify-center gap-1 overflow-auto p-0 sm:grid-cols-3 sm:gap-3"
        >
          <RouterLink
            v-for="result in results"
            class="relative m-0 flex h-[110px] max-h-[110px] max-w-[223px] flex-row items-start justify-start gap-2 rounded-lg border border-solid p-2 text-center text-slate-600 transition-colors hover:bg-slate-100"
            :key="result.id"
            :to="`/shelter/people/profile/${result._id.$oid}`"
          >
            <div class="flex w-full flex-col gap-1 overflow-hidden text-ellipsis text-nowrap text-start">
              <p class="flex items-center gap-1">
                <span
                  v-if="result?.seq_id"
                  class="overflow-hidden text-ellipsis text-xs font-semibold"
                >
                  {{ result.seq_id }} -
                </span>

                <span class="overflow-hidden text-ellipsis text-xs font-semibold">
                  {{ result.full_name || $t('common.unknown') }}
                </span>
              </p>

              <div
                v-if="result.addresses[0]?.address1"
                class="flex items-center gap-1 overflow-hidden text-ellipsis text-nowrap text-[10px]"
              >
                <AAddress
                  class="overflow-hidden text-ellipsis text-nowrap"
                  :address="result.addresses[0]"
                />
              </div>

              <div class="flex items-center gap-1 overflow-hidden text-ellipsis text-nowrap text-[10px]">
                <span class="overflow-hidden text-ellipsis text-[10px]">
                  {{ formatPhoneNumber(result.phones[0]?.phone || '') }}
                </span>
              </div>

              <div class="flex items-center gap-1 overflow-hidden text-ellipsis text-nowrap text-[10px]">
                <span class="overflow-hidden text-ellipsis text-[10px]">
                  {{ result.emails[0]?.email ? result.emails[0]?.email : '' }}
                </span>
              </div>
            </div>
          </RouterLink>
        </div>
      </div>

      <div v-else-if="mode === 'fill'">
        <div
          v-if="results.length"
          class="flex items-center justify-center gap-2 text-sm font-semibold text-yellow-600"
        >
          <p>
            {{
              $t('components.forms.errors.duplicatedPerson', { person: results.length }, results.length) +
              '. ' +
              $t('components.formFields.contact.selectToUse')
            }}.
          </p>

          <i class="pi pi-user-plus text-sm"></i>
        </div>

        <div
          class="mt-4 grid h-fit max-h-96 grid-cols-2 items-center justify-center gap-1 overflow-auto p-0 sm:grid-cols-3 sm:gap-3"
        >
          <div
            v-for="result in results"
            class="relative m-0 flex h-[110px] max-h-[110px] max-w-[223px] cursor-pointer flex-row items-start justify-start gap-2 rounded-lg border border-solid p-2 text-center text-slate-600 transition-colors hover:bg-slate-100"
            :key="result.id"
            @click="$emit('onUsePerson', result)"
          >
            <div class="flex w-full flex-col gap-1 overflow-hidden text-ellipsis text-nowrap text-start">
              <p class="flex items-center gap-1">
                <span
                  v-if="result?.seq_id"
                  class="overflow-hidden text-ellipsis text-xs font-semibold"
                >
                  {{ result.seq_id }} -
                </span>

                <span class="overflow-hidden text-ellipsis text-xs font-semibold">
                  {{ result.full_name || $t('common.unknown') }}
                </span>
              </p>
              <div
                v-if="result.addresses[0]?.address1"
                class="flex items-center gap-1 overflow-hidden text-ellipsis text-nowrap text-[10px]"
              >
                <AAddress
                  class="overflow-hidden text-ellipsis text-nowrap"
                  :address="result.addresses[0]"
                />
              </div>

              <div class="flex items-center gap-1 text-[10px]">
                <span class="overflow-hidden text-ellipsis text-[10px]">
                  {{ formatPhoneNumber(result.phones[0]?.phone || '') }}
                </span>
              </div>

              <div class="flex items-center gap-1 text-[10px]">
                <span class="overflow-hidden text-ellipsis text-[10px]">
                  {{ result.emails[0]?.email ? result.emails[0]?.email : '' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer class="flex w-full justify-end pt-4">
        <Button
          class="af-button"
          severity="secondary"
          @click="handleClose"
        >
          {{ $t('filters.options.skip') }}
        </Button>
      </footer>
    </div>
  </div>
</template>
